import FingerprintJS from "@fingerprintjs/fingerprintjs";
import React, { createContext, useCallback, useEffect, useRef } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { v4 as uuidv4 } from "uuid";

const PRODUCTION_SITE_URL = "https://aepsy.com";

export function isProductionApp(): boolean {
	return process.env.GATSBY_AEPSY_WEBSITE_URL === PRODUCTION_SITE_URL;
}

// 1000: Normal closure.
// 1006: Abnormal closure, usually due to network issues.
const WEBSOCKET_ABNORMAL_CLOSURE = 1006;

// TODO: add auto generated enum from generated/graphql.tsx
export enum AnalyticsEventType {
	GAppStart = "G_APP_START",
	GButtonClick = "G_BUTTON_CLICK",
	GCreateAction = "G_CREATE_ACTION",
	GPageVisit = "G_PAGE_VISIT",
	GUpdateAction = "G_UPDATE_ACTION",
	GViewAction = "G_VIEW_ACTION",
}

export interface TrackingProps {
	eventType: AnalyticsEventType;
	eventName?: string;
	data?: string | object;
	locale?: any;
}

export type TrackingInput = {
	eventType: AnalyticsEventType;
	eventName?: string;
	id: string;
	deviceId: string;
	userId?: string;
	collectedAt: Date;
	data: any;
	sessionId?: string;
};

interface TrackingContextProps {
	track: (input: TrackingProps) => void;
}

export const TrackingContext = createContext<TrackingContextProps>({
	track: () => {},
});

interface Props {
	children: React.ReactNode;
}

const DEFAULT_PHOENIXA_WEBSOCKET_URL = "wss://phoenixa-dev.aepsy.com";
const TRACKING_SESSION_KEY = "aepsy_b2b_tracking_session";
const TRACKING_SESSION_KEY_EXPIRED_AT = "aepsy_b2b_tracking_session_expired_at";
const TRACKING_EXPIRED_AT = 30 * 60 * 1000;

const TrackingProvider: React.FC<Props> = ({ children }) => {
	const timeout = useRef<NodeJS.Timeout | number | null>(null);
	const waitingMessages = useRef<TrackingInput[]>([]);
	const sessionId = useRef("");
	const deviceId = useRef("");
	const userId = useRef("");

	const onOpen = () => {
		timeout.current = setTimeout(() => {
			if (waitingMessages.current?.length > 0) {
				for (const message of waitingMessages.current) {
					sendJsonMessage({
						event: "events",
						data: [{ ...message, userId: userId.current }],
					});
				}
				waitingMessages.current = [];
			}
			clearTimeout(timeout.current as NodeJS.Timeout);
		}, 2000);
	};

	const { sendJsonMessage, readyState } = useWebSocket(
		process.env.GATSBY_PHOENIXA_WEBSOCKET_URL || DEFAULT_PHOENIXA_WEBSOCKET_URL,
		{
			retryOnError: isProductionApp(),
			shouldReconnect: () => true,
			reconnectAttempts: isProductionApp() ? 10 : 0,
			reconnectInterval: 30000,
			onOpen,
			share: true,
			onError: () => {
				// TODO: add sentry
				// Sentry.captureException(event);
			},
			onClose: (event) => {
				if (event.code === WEBSOCKET_ABNORMAL_CLOSURE) {
					// TODO: add sentry
					// Sentry.captureException(event);
				}
			},
		},
	);

	const waitingForTracking = (input: TrackingInput) => {
		waitingMessages.current = [...waitingMessages.current, input];
	};

	const getDeviceId = async () => {
		if (deviceId.current) return deviceId.current;

		try {
			// Initialize FingerprintJS
			const fp = await FingerprintJS.load();
			const result = await fp.get();
			deviceId.current = result.visitorId;
			return result.visitorId;
		} catch {
			// Fallback to UUID if fingerprinting fails
			const fallbackId = uuidv4();
			deviceId.current = fallbackId;
			return fallbackId;
		}
	};

	const track = async ({
		eventType,
		eventName,
		data,
		locale,
	}: TrackingProps) => {
		try {
			const deviceIdentifier = await getDeviceId();

			const mappedData =
				typeof data === "string" ? data : { ...data, language: locale };

			const input = {
				eventType,
				eventName,
				id: uuidv4(),
				deviceId: deviceIdentifier,
				sessionId: sessionId.current,
				collectedAt: new Date(),
				data: mappedData,
			};

			if (readyState === ReadyState.OPEN) {
				sendJsonMessage({
					event: "events",
					data: [input],
				});
			} else {
				waitingForTracking(input);
			}
		} catch {
			// TODO: add sentry
			// Sentry.captureException(e);
		}
	};

	const renewExpired = useCallback(async () => {
		await localStorage.setItem(
			TRACKING_SESSION_KEY_EXPIRED_AT,
			new Date().getTime().toString(),
		);
	}, []);

	const generateNewSessionId = useCallback(async () => {
		const newSessionId = uuidv4();
		await localStorage.setItem(TRACKING_SESSION_KEY, newSessionId);
		await renewExpired();
		sessionId.current = newSessionId;
	}, [renewExpired]);

	const syncSessionId = useCallback(async () => {
		const storedSessionId = localStorage.getItem(TRACKING_SESSION_KEY);

		if (storedSessionId) {
			const storedSessionExpiredAt = localStorage.getItem(
				TRACKING_SESSION_KEY_EXPIRED_AT,
			);

			if (
				Number(storedSessionExpiredAt) + TRACKING_EXPIRED_AT <
				new Date().getTime()
			) {
				await generateNewSessionId();
			} else {
				await renewExpired();
				sessionId.current = storedSessionId;
			}
		} else {
			await generateNewSessionId();
		}
	}, [generateNewSessionId, renewExpired]);

	useEffect(() => {
		if (readyState === ReadyState.CONNECTING) {
			syncSessionId();
		}
	}, [readyState, syncSessionId]);

	useEffect(() => {
		return () => {
			waitingMessages.current = [];
			if (timeout.current) {
				clearTimeout(timeout.current);
			}
		};
	}, []);

	return (
		<TrackingContext.Provider
			value={{
				track,
			}}
		>
			{children}
		</TrackingContext.Provider>
	);
};

export default TrackingProvider;
